import type { IconName } from "../../icons";
import { useUiText } from "../../locale/UiLocale";
import { DropdownMenu } from "../DropdownMenu/DropdownMenu";
import { IconButton } from "../IconButton/IconButton";
import { groupRowActions } from "./rowActionGroups";

/** Minimum width of the overflow menu, in px. */
const MENU_MIN_WIDTH = 220;

export interface RowAction {
  label: string;
  icon?: IconName;
  onSelect: () => void;
  tone?: "default" | "danger";
  disabled?: boolean;
  /** Render as an icon button in the row instead of inside the overflow menu. */
  inline?: boolean;
  hidden?: boolean;
}

export interface RowActionsProps {
  actions: RowAction[];
  /** Accessible name of the overflow button. */
  label?: string;
}

/** Trailing row actions: inline icon buttons plus an overflow menu for the rest. */
export function RowActions({ actions, label = "More actions" }: RowActionsProps): React.JSX.Element {
  const t = useUiText();
  const { inline, menu } = groupRowActions(actions);

  return (
    <div className="nb-row-actions">
      {inline.map((a) => (
        <IconButton
          key={a.label}
          size="sm"
          label={a.label}
          icon={a.icon!}
          tone={a.tone === "danger" ? "danger" : "default"}
          disabled={a.disabled}
          onClick={a.onSelect}
        />
      ))}
      {menu.length ? (
        <DropdownMenu
          ariaLabel={t(label)}
          items={menu}
          minWidth={MENU_MIN_WIDTH}
          trigger={({ ref, ...props }) => (
            <IconButton ref={ref} size="sm" label={t(label)} icon="moreH" tooltip={false} {...props} />
          )}
        />
      ) : null}
    </div>
  );
}
